import React, { memo } from 'react';
import { withStyles } from '@mui/styles';
import { Button, TextField, Paper, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { useDrop } from 'react-dnd';
import TrackItem, { TRACK_ITEM_WIDTH } from './TrackItem';
import globalStyles from './styles';
import { trackItemType, sessionItemType, defaultTrackType } from './constants';

const styles = {
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    height: 50,
    alignItems: 'center',
  },
  title: {
    ...globalStyles.itemWrapper,
    width: TRACK_ITEM_WIDTH,
    marginTop: 10,
    backgroundColor: 'lightGrey',
    padding: '0 20px',
    fontSize: 16,
    height: 70,
  },
  loopBack: {
    ...globalStyles.itemWrapper,
    width: TRACK_ITEM_WIDTH,
    marginTop: 10,
    padding: '10px 20px',
  },
  list: {
    ...globalStyles.list,
    marginTop: 10,
  },
  additionalSpace: {
    flexGrow: 2,
    minHeight: 100,
    background: '#f5f5f5',
    border: '1px dashed #ccc',
  },
};

const TrackColumn = ({
  track,
  handleMove,
  handleMoveToPosition,
  handleRenameSession,
  handleDrop,
  increaseNextTrackSessionId,
  handleRename,
  handleDeleteTrackSession,
  handleDeleteTrack,
  handleCopyTrack,
  handleSetLoopBack,
  handleSetHowTo,
  classes,
}) => {
  const orderedSessions = [...(track.sessions ?? [])].sort((a, b) => a.order - b.order);
  const loopBackValue = track.loopBackToTrackSessionId ?? '';
  const [, dropRef] = useDrop(() => ({
    accept: [trackItemType, sessionItemType],
    drop(dragItem) {
      handleDrop({ dragItem, targetTrackId: track.id });
      if (dragItem.trackId === null || dragItem.isNew) {
        increaseNextTrackSessionId();
      }
    },
  }));

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <Button
          size="small"
          color="primary"
          onClick={() => handleDeleteTrack({ trackId: track.id })}
          disabled={track.type === defaultTrackType}
        >
          delete
        </Button>
        <Button size="small" color="primary" onClick={() => handleCopyTrack({ trackId: track.id })}>
          copy
        </Button>
      </div>
      <Paper elevation={1} className={classes.title}>
        <TextField
          placeholder="Track name"
          fullWidth
          value={track.name}
          onChange={({ target: { value } }) => handleRename({ value, trackId: track.id })}
          helperText={track.answers?.join(', ') || 'No answers'}
        />
      </Paper>
      <Paper elevation={1} className={classes.loopBack}>
        <TextField
          placeholder="How to (HTML or plain text)"
          label="How To"
          fullWidth
          multiline
          minRows={3}
          value={track.howTo ?? ''}
          onChange={({ target: { value } }) =>
            handleSetHowTo({ trackId: track.id, howTo: value === '' ? null : value })
          }
        />
      </Paper>
      <Paper elevation={1} className={classes.loopBack}>
        <FormControl fullWidth size="small">
          <InputLabel id={`loop-back-label-${track.id}`}>Loop back to</InputLabel>
          <Select
            labelId={`loop-back-label-${track.id}`}
            label="Loop back to"
            value={loopBackValue}
            onChange={({ target: { value } }) =>
              handleSetLoopBack({
                trackId: track.id,
                loopBackToTrackSessionId: value === '' ? null : value,
              })
            }
          >
            <MenuItem value="">
              <em>— none (program ends) —</em>
            </MenuItem>
            {orderedSessions.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {`${s.order}) ${s.name || '(unnamed)'}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>
      <div className={classes.list}>
        {track.sessions.map((item) => (
          <TrackItem
            key={item.id}
            item={item}
            trackId={track.id}
            sessionCount={track.sessions.length}
            handleMove={handleMove}
            handleMoveToPosition={handleMoveToPosition}
            handleRenameSession={handleRenameSession}
            increaseNextTrackSessionId={increaseNextTrackSessionId}
            handleDeleteTrackSession={handleDeleteTrackSession}
          />
        ))}
      </div>
      <div className={classes.additionalSpace} ref={dropRef} />
    </div>
  );
};

export default memo(withStyles(styles)(TrackColumn));
